import React, { SetStateAction, useRef } from "react";
import { getMe, getUserId, moveNumber, removeLobbyIdFromSearchParams } from "../utils/utils";
import { useGame } from "../context/GameContext";
import {
    ErrorResponse,
    ForfeitAction,
    ForfeitType,
    JoinLobbyAction,
    MoveAction,
    NewLobbyAction,
    PingAction,
    PlayAgainRequestAction,
    PlayAgainResponseAction,
    PlayAgainResponseType,
    PlayerJoinedType,
    PlayerMoveType,
    RejoinedLobbyType,
    StartGameType,
} from "../types/multiplayer";
import { AllResponsesSchema } from "../types/schemas/responses";
import { MultiplayerState } from "../context/MultiplayerContext";
import { eventStartGame } from "../utils/gtag";
import { Chess, Move } from "chess.js";
import usePlayAudio from "../utils/audio/moveAudio";
import { MULTIPLAYER } from "../utils/constants";
import { removeSavedLobby, setSavedLobby } from "../utils/multiplayer/savedLobbyManager";
import { handleHome, restartGame } from "../utils/gameControls";

/**
 * Custom hook to handle WebSocket connections for multiplayer games
 */
export function useWebsocket(
    setMultiplayerState: React.Dispatch<SetStateAction<MultiplayerState>>,
    multiplayerState: MultiplayerState,
) {
    const userId = getUserId();
    const playAudio = usePlayAudio();
    const { setStatus, setGameState, setRemoteNavigation } = useGame();
    const wsRef = useRef<WebSocket | null>(null);

    /**
     * Returns the user to the main menu and clears their lobbyId
     */
    const handleReturnMainMenu = () => {
        removeLobbyIdFromSearchParams();
        disconnect();
        handleHome(setGameState, setRemoteNavigation, setMultiplayerState);
        restartGame(setGameState);
    };

    /**
     * Callback function for when a lobby is created by the backend.
     *
     * @param lobbyId the lobbyId of the lobby that was created
     */
    const onLobbyCreated = (lobbyId: string) => {
        setMultiplayerState((prev) => ({ ...prev, lobbyId, waitingForPlayers: true }));
        setStatus({ showStatusPage: false });
    };

    /**
     * Updates the state when a new player joins the game.
     *
     * @param data the new user info
     */
    const onPlayerJoined = (data: PlayerJoinedType) => {
        const isMe = data.newUser.userId === userId;

        setMultiplayerState((prev) => ({
            ...prev,
            playerColor: isMe ? data.newUser.color : prev.playerColor,
            p1: data.users.p1,
            p2: data.users.p2 ?? prev.p2,
        }));
    };

    /**
     * Called when a rejoined_lobby action is received from the server.
     * Rejoins the player to the lobby and re-syncs them to the game.
     *
     * @param data the data to used resync the player to the game
     */
    const onRejoinedLobby = (data: RejoinedLobbyType) => {
        setMultiplayerState((prev) => ({
            ...prev,
            playerColor: data.newUser.color,
            userId: data.newUser.userId,
            p1: data.users.p1,
            p2: data.users.p2 ?? prev.p2,
            showUserNamePage: false,
        }));

        // Sync player back to where they left off
        setGameState((prev) => ({
            ...prev,
            game: new Chess(data.game.fen),
            lastMove: null,
            showGame: true,
        }));
        setStatus({ showStatusPage: false });
    };

    /**
     * Updates state when the game is started.
     *
     * @param data the state of the game that started
     */
    const onStartGame = (data: StartGameType) => {
        setStatus({ showStatusPage: false });
        setMultiplayerState((prev) => ({
            ...prev,
            waitingForPlayers: false,
            showUserNamePage: false,
            playAgainRequest: {
                sentRequest: false,
                receivedRequest: false,
            },
            playAgainResponse: {
                sentResponse: false,
                receivedResponse: false,
            },
            forfeit: {
                isForfeit: false,
            },
        }));
        setGameState((prev) => ({
            ...prev,
            game: new Chess(data.game.fen),
            showGame: true,
            lastMove: null,
            capturedPieces: {
                white: [],
                black: [],
            },
        }));

        // Save the lobby to localStorage
        const me = getMe(multiplayerState);
        setSavedLobby(multiplayerState.lobbyId, me.username);
    };

    /**
     * Handles setting the client state to follow the
     * server game state after a move.
     *
     * @param data the updated game state after the move
     */
    const onPlayerMove = (data: PlayerMoveType) => {
        setGameState((curr) => {
            const clientFen = curr.game?.fen() ?? "";
            if (data.game.fen === clientFen) return curr;

            const serverMoveNumber = data.game.moveNumber;
            const clientMoveNumber = moveNumber(clientFen);

            if (serverMoveNumber !== clientMoveNumber) {
                // If the move number does not match, it reconcile to the server state.
                // Always assume server is correct
                console.warn(
                    `Game desync detected. Client move = ${clientMoveNumber}, server move = ${serverMoveNumber}`,
                );
            }

            const game = new Chess(data.game.fen);

            // Create a temporary game to get the last move that led to this position
            // This calculates the previous move so it can be displayed
            // on the opponents screen and this players screen
            let lastMove = null;
            if (curr.game) {
                const tempGame = new Chess(curr.game.fen());
                const possibleMoves = tempGame.moves({ verbose: true });

                // Find the move that would result in the current position
                for (const move of possibleMoves) {
                    const testGame = new Chess(tempGame.fen());
                    testGame.move(move);
                    if (testGame.fen() === data.game.fen) {
                        lastMove = move;
                        break;
                    }
                }
            }

            return {
                ...curr,
                game,
                lastMove,
            };
        });

        playAudio();
    };

    /**
     * Called when a forfeit event is received from the backend.
     *
     * @param data the data associated with the winner of the forfeit
     */
    const onForfeit = (data: ForfeitType) => {
        setMultiplayerState((prev) => ({
            ...prev,
            forfeit: {
                isForfeit: true,
                winner: data.winner,
            },
        }));
    };

    /**
     * Handles when the player requests to play again
     */
    const onPlayAgainRequest = () => {
        setMultiplayerState((prev) => ({
            ...prev,
            playAgainRequest: { ...prev.playAgainRequest, receivedRequest: true },
        }));
    };

    /**
     * Handles when the player responds to the request to play again
     *
     * @param data the response from the play again request
     */
    const onPlayAgainResponse = (data: PlayAgainResponseType) => {
        setMultiplayerState((prev) => ({
            ...prev,
            playAgainResponse: {
                ...prev.playAgainResponse,
                receivedResponse: true,
                response: data.response,
            },
        }));
    };

    /**
     * Handles errors from the server to update the
     * game UI and display errors to the player.
     *
     * @param data the error information
     */
    const onServerError = (data: ErrorResponse) => {
        switch (data.error) {
            case "full_game":
                setStatus({
                    showStatusPage: true,
                    showLoadingIndicator: false,
                    message: "Game is full.",
                });
                removeSavedLobby();
                break;
            case "game_not_found":
                setStatus({
                    showStatusPage: true,
                    showLoadingIndicator: false,
                    message: "Couldn't find that game!",
                });
                removeSavedLobby();
                break;
            default:
                console.error("Unknown error received: ", data);
                return;
        }
    };

    /**
     * Handles messages sent to the Websocket connection.
     *
     * @param msg the message received from the Websocket
     */
    const onMessage = (msg: any) => {
        const parsedData = JSON.parse(msg.data);

        const parsed = AllResponsesSchema.safeParse(parsedData);
        if (!parsed.success) {
            console.error(`Failed to parse incoming WS message: ${parsedData}, ${parsed.error}`);
            return;
        }

        const { data } = parsed;

        switch (data.type) {
            case "pong":
                break;
            case "lobby_created":
                onLobbyCreated(data.lobbyId);
                break;
            case "player_joined":
                onPlayerJoined(data);
                break;
            case "forfeit":
                onForfeit(data);
                break;
            case "play_again_request":
                onPlayAgainRequest();
                break;
            case "play_again_response":
                onPlayAgainResponse(data);
                break;
            case "player_move":
                onPlayerMove(data);
                break;
            case "rejoined_lobby":
                onRejoinedLobby(data);
                break;
            case "start_game":
                onStartGame(data);
                break;
            case "error":
                onServerError(data);
                break;
            default:
                console.error("Unknown type: ", data);
                return;
        }
    };

    /**
     * Called when the WS emits an error.
     *
     * @param err the error event
     */
    const onError = (err: any) => {
        console.error(err);
    };

    const connectToWS = () => {
        if (wsRef.current) wsRef.current.close();
        wsRef.current = null;

        const ws = new WebSocket(MULTIPLAYER.WS_URL);
        ws.onerror = onError;
        ws.onmessage = onMessage;
        wsRef.current = ws;

        const interval = setInterval(() => {
            if (!wsRef.current) {
                clearInterval(interval);
                return;
            }

            const msg = JSON.stringify({
                action: "ping",
            } satisfies PingAction);
            wsRef.current.send(msg);
        }, MULTIPLAYER.PING_INTERVAL);
    };

    /**
     * Handles hosting a new game by connecting to the WS
     * and sending the 'new_lobby' action.
     */
    const hostGame = () => {
        setStatus({
            showStatusPage: true,
            showLoadingIndicator: true,
            message: "Creating game...",
        });
        connectToWS();
        eventStartGame(2);
        setGameState((prev) => ({ ...prev, isAIGame: false }));
        setMultiplayerState((prev) => ({ ...prev, isHost: true }));

        if (!wsRef.current) {
            console.error("wsRef is null after connecting to WS");
            return;
        }

        const ws = wsRef.current;

        ws.onopen = () => {
            const msg = JSON.stringify({
                action: "new_lobby",
            } satisfies NewLobbyAction);
            ws.send(msg);
        };
    };

    /**
     * Joins a player to a game through the WS connection.
     *
     * @param lobbyId the lobbyId of the lobby to join
     * @param username the username to join the lobby with
     * @param isRejoin whether the player is rejoining an existing lobby
     * @param isHost whether the player is the current host of the game
     * @returns void
     */
    const joinGame = (lobbyId: string, username: string, isRejoin: boolean = false) => {
        setStatus({
            showStatusPage: true,
            showLoadingIndicator: true,
            message: isRejoin ? "Reconnecting to game..." : "Waiting for game to start...",
        });

        connectToWS();
        setMultiplayerState((prev) => ({
            ...prev,
            userId,
            lobbyId,
            showUserNamePage: false,
            isHost: false,
        }));

        if (!wsRef.current) {
            console.error("wsRef is null after connecting to WS");
            return;
        }

        const ws = wsRef.current;

        const msg = JSON.stringify({
            action: "join_lobby",
            userId,
            username,
            lobbyId,
            isRejoin,
        } satisfies JoinLobbyAction);
        ws.onopen = () => ws.send(msg);

        if (!isRejoin) setSavedLobby(lobbyId, username);
    };

    /**
     * Disconnects the player from the current WebSocket connection
     *
     * @returns void
     */
    const disconnect = () => {
        if (!wsRef.current) return;
        wsRef.current.close();
        wsRef.current = null;
    };

    /**
     * Sends a move to the backend to be verified
     *
     * @param move the move to send
     * @returns void
     */
    const sendMultiplayerMove = (move: Move) => {
        if (!wsRef.current) {
            console.error("Websocket is undefined: ", wsRef.current);
            return;
        }

        const msg = JSON.stringify({
            action: "move",
            lobbyId: multiplayerState.lobbyId,
            move: {
                isPromotion: move.isPromotion(),
                from: move.from,
                to: move.to,
                promotion: move.promotion ?? "",
            },
            moveNumber: moveNumber(move.before),
        } satisfies MoveAction);

        wsRef.current.send(msg);
    };

    const forfeitGame = () => {
        if (!wsRef.current) {
            console.error("Websocket is undefined");
            return;
        }

        const msg = JSON.stringify({
            action: "forfeit",
            lobbyId: multiplayerState.lobbyId,
        } satisfies ForfeitAction);

        wsRef.current.send(msg);
    };

    const requestToPlayAgain = () => {
        if (!wsRef.current) {
            console.error("Websocket is undefined");
            return;
        }

        const msg = JSON.stringify({
            action: "play_again_request",
            lobbyId: multiplayerState.lobbyId,
        } satisfies PlayAgainRequestAction);
        wsRef.current.send(msg);
        setMultiplayerState((prev) => ({
            ...prev,
            playAgainRequest: {
                ...prev.playAgainRequest,
                sentRequest: true,
            },
        }));
    };

    /**
     * Handles sending a response to the play again request.
     * If the request is denied, the user is returned to the
     * main menu and the game is deleted from state.
     *
     * @param response the response to the request to play again
     */
    const responseToPlayAgainRequest = (response: "accept" | "deny") => {
        if (!wsRef.current) {
            console.error("Websocket is undefined");
            return;
        }

        const msg = JSON.stringify({
            action: "play_again_response",
            lobbyId: multiplayerState.lobbyId,
            response,
        } satisfies PlayAgainResponseAction);
        wsRef.current.send(msg);

        if (response === "deny") handleReturnMainMenu();
    };

    return {
        hostGame,
        joinGame,
        disconnect,
        sendMultiplayerMove,
        forfeitGame,
        responseToPlayAgainRequest,
        requestToPlayAgain,
        handleReturnMainMenu,
    };
}
